import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import helpers from '../../Utils/helpers';

const SORT_KEYS = {
    sent: 'sentAt',
    subject: 'subject',
    type: 'type'
};

const COLUMN_COUNT = 8;

function CustomerEmailsList({ emails, onHistoryClick }) {
    const { t } = useTranslation();

    const [typeFilter, setTypeFilter] = useState('All');
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState('sent');
    const [sortDir, setSortDir] = useState('desc');
    const [groupByRecipient, setGroupByRecipient] = useState(true);
    const [expandedRecipients, setExpandedRecipients] = useState(() => new Set());

    const visibleEmails = useMemo(() => {
        if (!emails) return [];
        const term = search.trim().toLowerCase();
        const filtered = emails.filter(e => {
            if (typeFilter !== 'All' && e.type !== typeFilter) return false;
            if (!term) return true;
            const subject = (e.subject || '').toLowerCase();
            const recipient = (e.recipient || '').toLowerCase();
            return subject.includes(term) || recipient.includes(term);
        });

        const key = SORT_KEYS[sortKey] || SORT_KEYS.sent;
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const av = a[key];
            const bv = b[key];
            if (av === bv) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return av > bv ? dir : -dir;
        });
    }, [emails, typeFilter, search, sortKey, sortDir]);

    const groupedEmails = useMemo(() => {
        if (!groupByRecipient) return null;
        const groups = new Map();
        visibleEmails.forEach(e => {
            const key = (e.recipient || '').toLowerCase();
            if (!groups.has(key)) {
                groups.set(key, { recipient: e.recipient || '', items: [] });
            }
            groups.get(key).items.push(e);
        });
        // Sort groups by most-recent send (desc); items inside keep visibleEmails' sort.
        return Array.from(groups.values()).sort((a, b) => {
            const aLatest = Math.max(...a.items.map(i => new Date(i.sentAt).getTime() || 0));
            const bLatest = Math.max(...b.items.map(i => new Date(i.sentAt).getTime() || 0));
            return bLatest - aLatest;
        });
    }, [visibleEmails, groupByRecipient]);

    const toggleSort = (key) => {
        if (key === sortKey) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'sent' ? 'desc' : 'asc');
        }
    };

    const sortIndicator = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

    const toggleExpand = (recipientKey) => {
        setExpandedRecipients(prev => {
            const next = new Set(prev);
            if (next.has(recipientKey)) next.delete(recipientKey); else next.add(recipientKey);
            return next;
        });
    };

    const expandAll = () => {
        if (!groupedEmails) return;
        setExpandedRecipients(new Set(groupedEmails.map(g => g.recipient.toLowerCase())));
    };

    const collapseAll = () => setExpandedRecipients(new Set());

    const typeLabel = (type) => {
        const key = `customeremails.type.${(type || '').toLowerCase()}`;
        const translated = t(key);
        return translated === key ? type : translated;
    };

    const typeBadgeClass = (type) => {
        switch (type) {
            case 'Campaign': return 'label label-info';
            case 'Automation': return 'label label-primary';
            case 'Transactional': return 'label label-warning';
            default: return 'label label-default';
        }
    };

    const statusLabel = (status) => {
        if (!status) return '';
        const key = `customeremails.status.${status.toLowerCase()}`;
        const translated = t(key);
        return translated === key ? status : translated;
    };

    const statusBadgeClass = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'SENT': return 'label label-success';
            case 'DEFERRED': return 'label label-warning';
            case 'BOUNCE':
            case 'DROPPED': return 'label label-danger';
            default: return 'label label-default';
        }
    };

    const renderRow = (item, options = {}) => {
        const { hideRecipient = false } = options;
        return (
            <tr key={item.id}>
                <td className="text-left" style={hideRecipient ? { paddingLeft: 32 } : undefined}>
                    {hideRecipient ? '' : item.recipient}
                </td>
                <td className="text-left">{item.subject}</td>
                <td className="text-left">
                    <span className={typeBadgeClass(item.type)}>{typeLabel(item.type)}</span>
                </td>
                <td className="text-left">
                    <span className={statusBadgeClass(item.status)} title={item.statusDetail || ''}>
                        {statusLabel(item.status)}
                    </span>
                </td>
                <td className="text-center" title={item.firstOpenedAt ? `${t('customeremails.firstOpened')}: ${helpers.formatDateAndTime(item.firstOpenedAt)}` : ''}>
                    {item.openCount > 0 ? item.openCount : ''}
                </td>
                <td className="text-center">
                    {item.clickCount > 0 ? item.clickCount : ''}
                </td>
                <td className="text-left">{helpers.formatDateAndTime(item.sentAt)}</td>
                <td className="text-center infoColumn">
                    <button
                        className="btn btn-default"
                        onClick={() => onHistoryClick(item)}
                        title={t('customeremails.history.title')}
                    >
                        {t('customeremails.history.button')}
                    </button>
                </td>
            </tr>
        );
    };

    const renderGroupedRows = () => {
        const rows = [];
        groupedEmails.forEach(group => {
            const key = group.recipient.toLowerCase();
            const isExpanded = expandedRecipients.has(key);
            const latest = group.items.reduce((max, i) => {
                const t = new Date(i.sentAt).getTime();
                return t > max ? t : max;
            }, 0);
            rows.push(
                <tr
                    key={`group-${key}`}
                    onClick={() => toggleExpand(key)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleExpand(key);
                        }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={isExpanded}
                    aria-label={`${group.recipient} (${group.items.length})`}
                    style={{ cursor: 'pointer', backgroundColor: '#f5f5f5', fontWeight: 600 }}
                >
                    <td className="text-left" colSpan={COLUMN_COUNT - 1}>
                        <span style={{ marginRight: 8, display: 'inline-block', width: 12 }} aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
                        {group.recipient}
                        <span className="badge" style={{ marginLeft: 12 }}>
                            {group.items.length}
                        </span>
                    </td>
                    <td className="text-left">{latest ? helpers.formatDateAndTime(new Date(latest)) : ''}</td>
                </tr>
            );
            if (isExpanded) {
                group.items.forEach(item => rows.push(renderRow(item, { hideRecipient: true })));
            }
        });
        return rows;
    };

    return <div>
        <div className="row" style={{ marginBottom: 12 }}>
            <div className="col-md-4">
                <label className="control-label">{t('customeremails.filter.type')}</label>
                <select
                    className="form-control"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                >
                    <option value="All">{t('customeremails.filter.all')}</option>
                    <option value="Campaign">{t('customeremails.type.campaign')}</option>
                    <option value="Automation">{t('customeremails.type.automation')}</option>
                    <option value="Transactional">{t('customeremails.type.transactional')}</option>
                    <option value="Other">{t('customeremails.type.other')}</option>
                </select>
            </div>
            <div className="col-md-8">
                <label className="control-label">{t('customeremails.filter.search')}</label>
                <input
                    type="text"
                    className="form-control"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('customeremails.filter.search')}
                />
            </div>
        </div>
        <div className="row" style={{ marginBottom: 12 }}>
            <div className="col-md-12" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="checkbox" style={{ margin: 0 }}>
                    <input
                        id="customeremails-group-by-recipient"
                        type="checkbox"
                        checked={groupByRecipient}
                        onChange={(e) => setGroupByRecipient(e.target.checked)}
                    />
                    <label htmlFor="customeremails-group-by-recipient">
                        {t('customeremails.groupByRecipient')}
                    </label>
                </div>
                <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={expandAll}
                    style={{ visibility: groupByRecipient ? 'visible' : 'hidden' }}
                >
                    {t('customeremails.expandAll')}
                </button>
                <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={collapseAll}
                    style={{ visibility: groupByRecipient ? 'visible' : 'hidden' }}
                >
                    {t('customeremails.collapseAll')}
                </button>
            </div>
        </div>

        <div className="table-responsive">
            <table className="table table-stripe">
                <thead>
                    <tr>
                        <th className="text-left">{t('customeremails.recipient')}</th>
                        <th className="text-left" style={{ cursor: 'pointer' }} onClick={() => toggleSort('subject')}>
                            {t('customeremails.subject')}{sortIndicator('subject')}
                        </th>
                        <th className="text-left" style={{ cursor: 'pointer' }} onClick={() => toggleSort('type')}>
                            {t('customeremails.typeHeader')}{sortIndicator('type')}
                        </th>
                        <th className="text-left">{t('customeremails.statusHeader')}</th>
                        <th className="text-center">{t('customeremails.opens')}</th>
                        <th className="text-center">{t('customeremails.clicks')}</th>
                        <th className="text-left" style={{ cursor: 'pointer' }} onClick={() => toggleSort('sent')}>
                            {t('customeremails.sent')}{sortIndicator('sent')}
                        </th>
                        <th className="text-left"></th>
                    </tr>
                </thead>
                <tbody>
                    {groupByRecipient && groupedEmails
                        ? renderGroupedRows()
                        : visibleEmails.map((item) => renderRow(item))}
                </tbody>
            </table>
        </div>
    </div>;
}

export default CustomerEmailsList;
